// Purpose: Badge primitive for status + metadata labels in tables /
// cards. Distinct from StatusPill's dot-indicator: Badge is a solid-bg
// pill used inline in tables + lists.

import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  size?: BadgeSize;
  children: ReactNode;
}

const TONE: Record<BadgeTone, string> = {
  neutral: 'bg-bg-tertiary text-fg-secondary',
  success: 'bg-status-success-muted text-status-success',
  warning: 'bg-status-warning-muted text-status-warning',
  danger: 'bg-status-danger-muted text-status-danger',
  info: 'bg-status-info-muted text-status-info',
};

const SIZE: Record<BadgeSize, string> = {
  sm: 'px-2 h-[18px] text-caption',
  md: 'px-2 h-[22px] text-label',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = 'neutral', size = 'md', children, className, ...rest },
  ref
) {
  return (
    <span
      ref={ref}
      className={[
        'inline-flex items-center rounded-sm font-semibold uppercase',
        TONE[tone],
        SIZE[size],
        className ?? '',
      ].join(' ')}
      {...rest}
    >
      {children}
    </span>
  );
});
